import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames';
import { useIntl } from 'react-intl';
import ColorSelect from '../../../components/ColorSelect';
import AttributePicker from '../../../components/common/AttributePicker';
import messages from './messages';
import { priceToString } from '../../../utils';
import { getCookie } from '../../../utils/common/common';
import styles from './VariantSelection.module.scss';

import {
  ATTRIBUTE_DESCRIPTION_COOKIE,
  VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
  VARIANT_ATTRIBUTE_OUT_OF_STOCK,
  VARIANT_ATTRIBUTE_NAME_SIZES,
  FEEL_ATTRIBUTE_NAME,
  KID_SHEETS_COLOR_KEY,
  SOFTSTRETCH_SHEETS_KEY,
} from './constants';

import { isColorAttribute, getOptions } from './utils';
import AttributeModal from '../../../components/AttributeModal';
import Tooltip from '../../../components/common/Tooltip/Tooltip';
import SizeSelection from '../SizeSelection';

const getAttributeTitleWithPrefix = ({ title, prefix }) => {
  if (!prefix) {
    return title;
  }

  const firstWordOfTitle = title.split(' ')[0] ?? '';
  if (firstWordOfTitle?.toLowerCase() === prefix.toLowerCase()) {
    return title;
  }
  return `${prefix} ${title}`;
};

const VARIANT_ATTRIBUTES_TO_FILTER = [
  VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
  VARIANT_ATTRIBUTE_OUT_OF_STOCK,
];

const VariantSelection = ({
  variants = [],
  defaultSelectedSKU = undefined,
  onSelectVariant,
  isShowingPriceDetails = true,
  isShowingDiscountedPrices = false,
  productKey = null,
  productName = null,
  attributesExtraInfo = [],
  attributesUpperTitle = undefined,
  discountSavings = undefined,
  attributeTitlePrefix = undefined,
  sortColors = true,
}) => {
  const sizeRef = useRef(null);
  const randomId = useMemo(() => Math.floor(Math.random() * 10000), []);
  const cookie = getCookie(ATTRIBUTE_DESCRIPTION_COOKIE);
  const isAttributeDescription =
    cookie?.value === 'show_description_test_pdp_selectors';
  const [colorDisclaimer, setColorDisclaimer] = useState(false);

  useEffect(() => {
    if (
      sizeRef?.current?.scrollTo &&
      sizeRef?.current.scrollWidth > sizeRef?.current.clientWidth
    ) {
      const containerClientWidth = sizeRef.current.clientWidth;
      const optionSelected = sizeRef.current.querySelector(
        '[data-selected=true]',
      );
      const optionSelectedWidth = optionSelected?.clientWidth;
      const optionOffsetLeft = optionSelected?.offsetLeft;

      if (optionSelected) {
        const scrollX =
          optionOffsetLeft + optionSelectedWidth / 2 - containerClientWidth / 2;

        sizeRef.current.scrollTo({
          top: 0,
          left: scrollX,
          behavior: 'smooth',
        });
      }
    }
  });

  const { formatMessage, formatNumber, locale } = useIntl();

  const [selectedAttributeValues, setSelectedAttributeValues] = useState({});
  const [
    initiallySelectedAttributeValues,
    setInitiallySelectedAttributeValues,
  ] = useState();
  const [productVariantName, setProductVariantName] = useState(undefined);

  const { options, attributeNames } = getOptions(variants, locale);

  const orderAttributeNames = (attrNames) =>
    [
      ...attrNames.filter(
        (attrName) => !isColorAttribute({ attributeName: attrName }),
      ),
      attrNames.find((attrName) =>
        isColorAttribute({ attributeName: attrName }),
      ),
    ].filter(Boolean);

  const findVariantByAttributeValues = useCallback(
    (attributeValues) =>
      variants?.find(({ attributes }) =>
        attributes
          .filter(({ name }) => !VARIANT_ATTRIBUTES_TO_FILTER.includes(name))
          .every((attr) =>
            Object.entries(attributeValues).find(
              ([selectedAttrName, selectedAttrValue]) =>
                selectedAttrName === attr.name &&
                selectedAttrValue?.key === attr.key,
            ),
          ),
      ),
    [variants],
  );

  const setNewSelectedAttributeValuesStateHandler = (
    newSelectedAttributeValuesState,
  ) => {
    const variantAttributeKey =
      selectedAttributeValues?.feel?.key[0].toUpperCase();
    setSelectedAttributeValues(newSelectedAttributeValuesState);
    setProductVariantName(
      `${productName?.replace(
        /\s/g,
        '',
      )}${variantAttributeKey}${selectedAttributeValues?.feel?.key.slice(1)}er`,
    );

    onSelectVariant({
      sku: findVariantByAttributeValues(newSelectedAttributeValuesState)?.sku,
    });
  };

  const getPriceText = useCallback(
    ({ variant, attributeName }) => {
      const attributesNames = variants[0].attributes
        .map((attribute) => attribute.name.toLowerCase())
        .filter(
          (name) =>
            !isColorAttribute({ attributeName: name }) &&
            name !== VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
        );

      if (!variant.price) {
        return { discountedPriceAsText: '', priceAsText: '' };
      }
      if (
        attributesNames.length === 1 ||
        attributeName.toLowerCase().indexOf('size') >= 0
      ) {
        const variantPrice = variant.price;
        const variantDiscount = variant.discount ? variant.discount : undefined;
        const discountedPrice = {
          ...variantPrice,
          centAmount: variantPrice.centAmount,
        };
        const price = {
          ...variantPrice,
          centAmount:
            variantPrice.centAmount + variantDiscount?.centAmount ?? 0,
        };

        return {
          discountedPriceAsText: priceToString({
            price: discountedPrice,
            maximumFractionDigits: 0,
            formatNumber,
            formatMessage,
          }),
          discountedPrice,
          priceAsText:
            variantDiscount &&
            priceToString({
              price,
              maximumFractionDigits: 0,
              formatNumber,
              formatMessage,
              forceDigit: true,
            }),
          price,
        };
      }
      return { discountedPriceAsText: '', priceAsText: '' };
    },
    [variants, formatNumber, formatMessage],
  );

  useEffect(() => {
    // finds defaultSelectedVariant, Mastervariant and first variant
    const foundVariants = variants
      .filter((variant) => !variant.isOutOfStock)
      .reduce(
        (acc, curr, index) => ({
          ...acc,
          ...(curr.masterVariant === true ? { masterVariant: curr } : {}),
          ...(curr.sku === defaultSelectedSKU ? { defaultSelected: curr } : {}),
          ...(index === 0 ? { first: curr } : {}),
        }),
        {},
      );

    const variant =
      foundVariants.defaultSelected ||
      foundVariants.masterVariant ||
      foundVariants.first;

    const masterVariantAttributeValues = variant?.attributes?.reduce(
      (acc, { name, key }) => ({
        ...acc,
        [name]: { key },
      }),
      {},
    );

    setSelectedAttributeValues((prev) => {
      if (
        Object.keys(masterVariantAttributeValues).every(
          (key) =>
            JSON.stringify(prev[key]) ===
            JSON.stringify(masterVariantAttributeValues[key]),
        )
      ) {
        return prev;
      }
      return masterVariantAttributeValues;
    });
    if (!initiallySelectedAttributeValues) {
      setInitiallySelectedAttributeValues(masterVariantAttributeValues);
    }
    const variantAttributeKey =
      masterVariantAttributeValues?.feel?.key[0].toUpperCase();
    const variantFeelKey = masterVariantAttributeValues?.feel?.key.slice(1);
    setProductVariantName(
      `${productName?.replace(
        /\s/g,
        '',
      )}${variantAttributeKey}${variantFeelKey}er`,
    );
  }, [
    defaultSelectedSKU,
    initiallySelectedAttributeValues,
    productName,
    variants,
  ]);

  const isKidsSheets = (color) =>
    !!KID_SHEETS_COLOR_KEY.find((kidsColor) => kidsColor === color);

  useEffect(() => {
    if (
      productKey &&
      productKey === SOFTSTRETCH_SHEETS_KEY &&
      selectedAttributeValues?.color?.key &&
      isKidsSheets(selectedAttributeValues.color.key)
    ) {
      setColorDisclaimer(true);
    } else {
      setColorDisclaimer(false);
    }
  }, [productKey, selectedAttributeValues]);

  const renderAttributeExtraInfo = ({ attributeName }) => {
    const data = attributesExtraInfo.find(
      (extraInfo) => extraInfo.attributeName === attributeName,
    );
    if (!data) {
      return null;
    }

    const title = data.text || (
      <div
        className={styles['info-icon']}
        onClick={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        role="button"
        aria-label={attributeName}
        tabIndex="0"
      />
    );

    if (data.isToolTip) {
      return (
        <Tooltip direction="right-edge" align="bottom" content={data.content}>
          {title}
        </Tooltip>
      );
    }
    return (
      <AttributeModal
        title={title}
        content={data.content}
        backButtonLabel={productName}
        type={data.modalType}
      />
    );
  };

  const renderDiscountElement = ({ attributeName, variant }) => {
    const price = getPriceText({
      variant,
      attributeName,
    })?.priceAsText;

    const discountedPrice = getPriceText({
      variant,
      attributeName,
    })?.discountedPriceAsText;

    const priceAndDiscountedPriceAreEqual = price === discountedPrice;

    return (
      <div className={styles['discount-element-container']}>
        <div
          className={classnames({
            [styles['discount-element-original-price']]: true,
            [styles['discount-element-original-price-strikethrough']]:
              price && discountedPrice && !priceAndDiscountedPriceAreEqual,
          })}
        >
          {price && !priceAndDiscountedPriceAreEqual ? price : discountedPrice}
        </div>

        {price && !priceAndDiscountedPriceAreEqual && (
          <div className={styles['discount-element-discounted-price']}>
            {discountedPrice}
          </div>
        )}
      </div>
    );
  };
  return attributeNames?.length ? (
    <div className={styles.container}>
      {orderAttributeNames(attributeNames)?.map((attributeName) => {
        const uniqueKey = `VariantSelection_${attributeName}_${randomId}`;

        if (isColorAttribute({ attributeName })) {
          const selectedColor = options[attributeName]?.value?.find(
            (attributeValue) =>
              attributeValue.key ===
              selectedAttributeValues?.[attributeName]?.key,
          );
          const colorOptions = options?.[attributeName]?.value?.map(
            (value) => ({
              ...value,
              colorHex: options?.[
                VARIANT_ATTRIBUTE_NAME_COLOR_CODE
              ]?.value?.find((code) => code.key === value.key)?.label,
            }),
          );
          const colorOptionsWithValidVariantWithoutSort = colorOptions
            .filter((currentColor) =>
              findVariantByAttributeValues({
                ...selectedAttributeValues,
                [attributeName]: { key: currentColor.key },
              }),
            )
            .map((currentColor) => {
              const foundVariant = findVariantByAttributeValues({
                ...selectedAttributeValues,
                [attributeName]: { key: currentColor.key },
              });
              return {
                ...currentColor,
                isOutOfStock: foundVariant?.isOutOfStock,
                subheadTextForColor: foundVariant?.subheadTextForColor,
              };
            });

          const colorOptionsWithValidVariant = sortColors
            ? colorOptionsWithValidVariantWithoutSort.sort((color) => {
                if (
                  initiallySelectedAttributeValues &&
                  color.key === initiallySelectedAttributeValues?.color?.key
                ) {
                  return -1;
                }
                if (color.isOutOfStock) {
                  return 1;
                }
                return 0;
              })
            : colorOptionsWithValidVariantWithoutSort;

          return (
            <div
              className={styles['attribute-container-color']}
              key={uniqueKey}
            >
              <div className={styles['attribute-container-color-title']}>
                <div
                  className={styles['attribute-container-color-title-label']}
                >
                  {getAttributeTitleWithPrefix({
                    title: formatMessage(messages.color),
                    prefix: attributeTitlePrefix,
                  })}
                </div>
                <div
                  className={styles['attribute-container-color-title-value']}
                >
                  {selectedColor?.label}
                </div>
              </div>
              <ColorSelect
                key={attributeName}
                colors={colorOptionsWithValidVariant}
                onChange={(item) => {
                  setNewSelectedAttributeValuesStateHandler({
                    ...selectedAttributeValues,
                    [attributeName]: item,
                  });
                }}
                selectedColor={{
                  key: selectedColor?.key ?? '',
                  label: selectedColor?.label ?? '',
                }}
                showColorLabel={false}
                hasDisclaimer={colorDisclaimer}
              />
            </div>
          );
        }
        if (!VARIANT_ATTRIBUTES_TO_FILTER.includes(attributeName)) {
          return (
            <div className={styles['attribute-container']} key={uniqueKey}>
              {attributesUpperTitle && (
                <div className={styles['attribute-upper-title']}>
                  {attributesUpperTitle}
                </div>
              )}
              <div
                className={classnames(styles['attribute-title-container'], {
                  [styles['bottom-margin']]: attributeName === 'size',
                })}
              >
                <div className={styles['attribute-title-content']}>
                  <div
                    className={classnames(styles['attribute-title'], {
                      [styles['no-margin']]: attributeName === 'size',
                    })}
                  >
                    {options?.[attributeName]?.label?.[locale]
                      .split('-')
                      .join(' ')}
                  </div>
                  {renderAttributeExtraInfo({
                    attributeName: options?.[attributeName]?.label?.[locale],
                  })}
                </div>
                {productVariantName !== undefined &&
                  attributeName === FEEL_ATTRIBUTE_NAME &&
                  isAttributeDescription && (
                    <span className={styles['attribute-description-text']}>
                      {formatMessage(messages[productVariantName])}
                    </span>
                  )}
                {discountSavings && attributeName !== FEEL_ATTRIBUTE_NAME && (
                  <span className={styles['attribute-savings-banner']}>
                    {formatMessage(messages.variantSavings, {
                      price: discountSavings,
                    })}
                  </span>
                )}
              </div>
              {VARIANT_ATTRIBUTE_NAME_SIZES.includes(attributeName) ? (
                <SizeSelection
                  sizes={(options?.[attributeName]?.value ?? [])
                    .map((attributeValue) => {
                      const foundVariant = findVariantByAttributeValues({
                        ...selectedAttributeValues,
                        [attributeName]: { key: attributeValue.key },
                      });
                      const isOutOfStock = !!foundVariant?.isOutOfStock;

                      return foundVariant
                        ? {
                            key: attributeValue.key,
                            label: attributeValue.label,
                            ...getPriceText({
                              variant: foundVariant,
                              attributeName,
                            }),
                            isOutOfStock,
                          }
                        : null;
                    })
                    .filter((option) => option)}
                  selectedKey={selectedAttributeValues?.[attributeName]?.key}
                  onSelect={(selectedKey) => {
                    setNewSelectedAttributeValuesStateHandler({
                      ...selectedAttributeValues,
                      [attributeName]: { key: selectedKey },
                    });
                  }}
                />
              ) : (
                <div
                  ref={attributeName === 'size' ? sizeRef : undefined}
                  className={classnames({
                    [styles['attribute-values-container']]: true,
                    [styles['attribute-values-container-1item']]:
                      options?.[attributeName]?.value?.length === 1,
                    [styles['attribute-values-container-2items']]:
                      options?.[attributeName]?.value?.length === 2,
                    [styles['attribute-values-container-3items-or-more']]:
                      options?.[attributeName]?.value?.length >= 3,
                  })}
                >
                  {options?.[attributeName]?.value?.map((attributeValue) => {
                    const foundVariant = findVariantByAttributeValues({
                      ...selectedAttributeValues,
                      [attributeName]: { key: attributeValue.key },
                    });
                    const isOutOfStockVariant = !!foundVariant?.isOutOfStock;

                    if (foundVariant) {
                      return (
                        <AttributePicker
                          key={attributeValue.key}
                          valueKey={attributeValue.key}
                          topLabel={attributeValue.label}
                          selected={
                            attributeValue.key ===
                            selectedAttributeValues?.[attributeName]?.key
                          }
                          onSelect={(selectedKey) => {
                            setNewSelectedAttributeValuesStateHandler({
                              ...selectedAttributeValues,
                              [attributeName]: { key: selectedKey },
                            });
                          }}
                          mediumLabel={
                            isShowingPriceDetails && !isShowingDiscountedPrices
                              ? getPriceText({
                                  variant: foundVariant,
                                  attributeName,
                                })?.priceAsText
                              : undefined
                          }
                          mediumElement={
                            isShowingPriceDetails && isShowingDiscountedPrices
                              ? renderDiscountElement({
                                  variant: foundVariant,
                                  attributeName,
                                })
                              : undefined
                          }
                          isOutOfStock={isOutOfStockVariant}
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          );
        }
        return null;
      })}
    </div>
  ) : null;
};

VariantSelection.propTypes = {
  variants: PropTypes.arrayOf(PropTypes.object),
  defaultSelectedSKU: PropTypes.string,
  onSelectVariant: PropTypes.func.isRequired,
  isShowingPriceDetails: PropTypes.bool,
  isShowingDiscountedPrices: PropTypes.bool,
  attributesExtraInfo: PropTypes.arrayOf(
    PropTypes.shape({
      attributeName: PropTypes.string.isRequired,
      isToolTip: PropTypes.bool.isRequired,
      text: PropTypes.string,
      content: PropTypes.string.isRequired,
    }),
  ),
  attributesUpperTitle: PropTypes.string,
  productKey: PropTypes.string,
  productName: PropTypes.string,
  discountSavings: PropTypes.string,
  attributeTitlePrefix: PropTypes.string,
  sortColors: PropTypes.bool,
};

export default VariantSelection;
